import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { congesApi } from '../../services/conges-api';
import { BoutonPrincipal, BoutonSecondaire, Champ, Saisie, Section } from '../../components/ndf/ui/Champ';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { erreurGlobale, messageRefusPiece } from '../../components/ndf/ui/erreurs-champs';
import { useToast } from '../../context/ToastContext';
import { COULEUR_STATUT, LIBELLE_STATUT } from './CongesDemandesPage';
import { periodeFr } from '../../lib/periode-conges';
import { refusDuJustificatif } from '../../lib/piece-jointe';

/**
 * Le détail d'une demande, avec sa chronologie (R-CIR-04).
 *
 * Les actions proposées viennent du SERVEUR : il rend les rôles de l'appelant
 * face à cette demande précise, et l'écran n'affiche que ce qui a une chance
 * d'aboutir. Un bouton qui échoue une fois sur deux vaut moins que pas de
 * bouton du tout.
 *
 * La chronologie n'est pas un journal technique : c'est ce qui permet à un
 * salarié de comprendre pourquoi sa demande a été refusée, et à la RH de
 * défendre une décision six mois plus tard.
 */

const LIBELLE_COMPTEUR: Record<string, string> = {
  CP_N1: 'Congés payés N-1',
  CP_N: 'Congés payés année en cours',
  RTT: 'RTT',
  EXAMENS: 'Congé Examens',
  ABS_AUT: 'Absence autorisée',
};

const LIBELLE_EVENEMENT: Record<string, string> = {
  conges_t1: 'Demande envoyée',
  conges_t2: 'Approuvée par le manager',
  conges_t3: 'Approuvée par la RH',
  conges_t4: 'Rejetée par le manager',
  conges_t5: 'Rejetée par la RH',
  conges_t6: 'Annulée par le salarié',
  conges_t7: 'Annulée par la RH',
  conges_alerte_plafond: 'Alerte de dépassement signalée au valideur',
  conges_correction_compteur: 'Correction manuelle de compteur',
  conges_justificatif_depose: 'Justificatif déposé',
  conges_justificatif_valide: 'Justificatif validé par la RH',
  conges_justificatif_refuse: 'Justificatif refusé par la RH',
};

export function CongesDemandeDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [motif, setMotif] = useState('');
  const [action, setAction] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['conges', 'demande', id],
    queryFn: () => congesApi.demandes.detail(id),
  });

  // Le dossier de décision n'est chargé que pour qui peut décider : le salarié
  // n'a pas à voir les absences de ses collègues sur la période.
  const estValideur = Boolean(
    data && (data.roles.includes('manager') || data.roles.includes('rh')),
  );
  const { data: dossier } = useQuery({
    queryKey: ['conges', 'dossier', id],
    queryFn: () => congesApi.demandes.dossier(id),
    enabled: estValideur,
  });

  /**
   * Le dépôt d'un justificatif APRÈS l'absence (R-ABS-07).
   *
   * C'est le chemin qui manquait : la pièce était exigée à la soumission, et
   * rien ne permettait de l'attacher ensuite. Un acte de décès étant remis le
   * jour des obsèques, le salarié n'avait aucune issue.
   */
  const deposer = useMutation({
    mutationFn: (f: { filename: string; contenu_base64: string; mime?: string | null }) =>
      congesApi.demandes.deposerJustificatif(id, f),
    onSuccess: () => {
      toast.success('Justificatif déposé. La RH est prévenue, elle le vérifiera.');
      void qc.invalidateQueries({ queryKey: ['conges', 'demande', id] });
    },
    onError: (e) => toast.error(messageRefusPiece(e) ?? erreurGlobale(e) ?? 'Le dépôt a échoué.'),
  });

  const choisirFichier = (fichier: File | null, champ?: HTMLInputElement | null) => {
    if (!fichier) return;
    const refus = refusDuJustificatif(fichier);
    if (refus) {
      toast.error(refus);
      // Vidé, sinon rechoisir le MÊME fichier ne déclenche plus rien et l'écran
      // paraît muet — exactement le refus silencieux qu'on vient de corriger.
      if (champ) champ.value = '';
      return;
    }
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const brut = String(lecteur.result ?? '');
      deposer.mutate({ filename: fichier.name, contenu_base64: brut.split(',')[1] ?? '', mime: fichier.type });
    };
    lecteur.readAsDataURL(fichier);
  };

  const transiter = useMutation({
    mutationFn: (t: { code: string; motif?: string }) =>
      congesApi.demandes.transition(id, t.code, t.motif),
    onSuccess: () => {
      toast.success('C’est fait.');
      setAction(null);
      setMotif('');
      qc.invalidateQueries({ queryKey: ['conges'] });
    },
    onError: (e) => toast.error(erreurGlobale(e) ?? 'L’action a échoué.'),
  });

  /**
   * Le justificatif s'ouvre EN PLEIN ÉCRAN, dans un onglet.
   *
   * Un valideur regarde une pièce pour la lire, pas pour deviner ce qu'elle
   * contient dans une vignette. Le contenu vient de l'API et non d'une URL
   * publique : la pièce reste protégée par la session.
   */
  const ouvrirPiece = async (pieceId: string) => {
    try {
      const p = await congesApi.demandes.justificatif(id, pieceId);
      const binaire = atob(p.contenu_base64);
      const octets = new Uint8Array(binaire.length);
      for (let i = 0; i < binaire.length; i++) octets[i] = binaire.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([octets], { type: p.mime ?? 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Le justificatif n’a pas pu être ouvert.');
    }
  };

  if (isLoading) return <p className="p-6 text-nuit/60">Chargement…</p>;
  if (error) return <div className="p-6"><MessageErreur erreur={error} /></div>;
  if (!data) return null;

  const { demande: d, roles, chronologie, liee, justificatifs } = data;
  const estMoi = roles.includes('collaborateur');
  const estManager = roles.includes('manager');
  const estRh = roles.includes('rh');

  // Les actions offertes découlent du statut ET du rôle : exactement ce que la
  // machine à états acceptera côté serveur.
  const actions: Array<{ code: string; libelle: string; motif: boolean; danger?: boolean }> = [];
  if (estMoi && (d.statut === 'attente_manager' || d.statut === 'attente_rh')) {
    actions.push({ code: 'T6', libelle: 'Annuler ma demande', motif: false });
  }
  if (estManager && d.statut === 'attente_manager') {
    actions.push({ code: 'T2', libelle: 'Approuver', motif: false });
    actions.push({ code: 'T4', libelle: 'Rejeter', motif: true, danger: true });
  }
  if (estRh && d.statut === 'attente_rh') {
    actions.push({ code: 'T3', libelle: 'Approuver', motif: false });
    actions.push({ code: 'T5', libelle: 'Rejeter', motif: true, danger: true });
  }
  if (estRh && d.statut === 'approuvee') {
    actions.push({ code: 'T7', libelle: 'Annuler cette absence approuvée', motif: true, danger: true });
  }

  /** Le retour, identique en tête et en pied : on ne le cherche jamais. */
  const retour = (
    <BoutonSecondaire
      type="button"
      onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/conges/demandes'))}
    >
      ← Retour
    </BoutonSecondaire>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 md:p-6">
      {/* EN TÊTE, obligatoirement : placé en pied, il fallait dérouler toute la
          fiche pour revenir. Doublé en bas, la fiche étant longue. */}
      <div>{retour}</div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">{d.type_libelle ?? 'Absence'}</h1>
          <p className="text-sm text-nuit/60">{periodeFr(d)} ({d.nb_jours} jour(s))</p>
        </div>
        <span
          className={`rounded-full px-3 py-1 text-sm font-medium ${
            COULEUR_STATUT[d.statut] ?? 'bg-slate-100 text-slate-700'
          }`}
        >
          {LIBELLE_STATUT[d.statut] ?? d.statut}
        </span>
      </header>

      {d.a_posteriori && (
        <p className="rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-jaune">
          Absence déclarée après coup. Le manager et la RH en ont été prévenus.
          {d.a_regulariser_paie && ' Elle est marquée « à régulariser en paie ».'}
        </p>
      )}

      {/* R-ABS-04 : le lien est visible des deux côtés, pour le salarié comme
          pour les valideurs : les sorts sont indépendants, mais la décision se
          prend en sachant que l'autre existe. */}
      {liee && (
        <p className="rounded-lg border border-violet-fond/60 bg-white p-3 text-sm text-nuit/80">
          Demande liée :{' '}
          <Link to={`/conges/demandes/${liee.id}`} className="font-medium text-violet underline">
            {liee.type_libelle ?? 'Absence'}, {periodeFr(liee)}
          </Link>{' '}
          ({LIBELLE_STATUT[liee.statut] ?? liee.statut}). Les deux demandes suivent chacune leur
          circuit.
        </p>
      )}

      <Section titre="Vue d’ensemble" teinte="ciel">
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-nuit/60">Période</dt>
            <dd className="text-nuit">{periodeFr(d)}</dd>
          </div>
          <div>
            <dt className="text-nuit/60">Jours décomptés</dt>
            <dd className="tabular-nums text-nuit">{d.nb_jours}</dd>
            {/* Un nombre de jours sans son compteur laisse croire qu'on perd
                des congés payés, y compris sur un arrêt maladie qui n'en
                consomme aucun. Toute absence affichée dit son effet. */}
            <dd className="mt-0.5 text-xs text-nuit/60">
              {d.compteur
                ? `Décomptés du compteur ${LIBELLE_COMPTEUR[d.compteur] ?? d.compteur}.`
                : 'Ce type d’absence ne consomme aucun compteur de congés.'}
            </dd>
          </div>
          {d.description && (
            <div className="sm:col-span-2">
              <dt className="text-nuit/60">Description</dt>
              <dd className="text-nuit">{d.description}</dd>
            </div>
          )}
          {d.commentaire && (
            <div className="sm:col-span-2">
              <dt className="text-nuit/60">Commentaire</dt>
              <dd className="text-nuit">{d.commentaire}</dd>
            </div>
          )}
          {d.motif_decision && (
            <div className="sm:col-span-2">
              <dt className="text-nuit/60">Motif de la décision</dt>
              <dd className="text-corail">{d.motif_decision}</dd>
            </div>
          )}
          {/* Justificatif différé : l'état de la pièce, et le moyen de la déposer. Sans ce
              bloc, « justificatif attendu » serait un état que personne ne voit
              et que rien ne permet de lever. */}
          {d.justificatif_statut && (
            <div className="sm:col-span-2 rounded-lg border border-nuit/10 bg-ciel/10 p-3">
              <dt className="font-medium text-nuit">
                {d.justificatif_statut === 'attendu' && 'Justificatif attendu'}
                {d.justificatif_statut === 'depose' && 'Justificatif déposé — la RH doit le vérifier'}
                {d.justificatif_statut === 'valide' && 'Justificatif validé'}
                {d.justificatif_statut === 'refuse' && 'Justificatif refusé, à remplacer'}
              </dt>
              <dd className="mt-1 text-sm text-nuit/70">
                {d.justificatif_motif && <p className="text-corail">Motif : {d.justificatif_motif}</p>}
                {(d.justificatif_statut === 'attendu' || d.justificatif_statut === 'refuse') && (
                  <>
                    <p>
                      Cette pièce n’existe souvent qu’après l’événement : déposez-la dès que vous
                      l’avez. Sans elle, l’absence pourra être requalifiée en congé payé.
                    </p>
                    {(estMoi || estRh) && (
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="mt-2 block text-sm"
                        disabled={deposer.isPending}
                        onChange={(e) => choisirFichier(e.target.files?.[0] ?? null, e.target)}
                      />
                    )}
                  </>
                )}
              </dd>
            </div>
          )}
          {justificatifs.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="text-nuit/60">Justificatif</dt>
              <dd>
                {justificatifs.map((j) => (
                  <button
                    key={j.id}
                    type="button"
                    onClick={() => ouvrirPiece(j.id)}
                    className="text-violet underline"
                  >
                    {j.filename}
                  </button>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </Section>

      {/* R-VAL-10 : tout ce qu'il faut pour trancher, sur un seul écran. Aller
          chercher un de ces éléments ailleurs, c'est ne pas l'aller chercher. */}
      {dossier && (
        <Section titre="Pour décider" teinte="jaune">
          {dossier.alertes.length > 0 && (
            <div className="mb-3 rounded-lg border border-jaune-fond/60 border-l-4 border-l-jaune-fond bg-white p-3 text-sm text-jaune">
              {dossier.alertes.map((a, i) => (
                <p key={i}>{a}</p>
              ))}
            </div>
          )}

          <h3 className="mb-2 text-sm font-semibold text-nuit">
            Soldes de {dossier.salarie?.prenom} {dossier.salarie?.nom}
          </h3>
          {dossier.compteur ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-nuit/60">
                  <tr>
                    <th className="py-1 pr-4 font-medium">Compteur</th>
                    <th className="py-1 pr-4 font-medium">Aujourd’hui</th>
                    <th className="py-1 pr-4 font-medium">En attente</th>
                    <th className="py-1 pr-4 font-medium">Si vous approuvez</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(dossier.soldes.avant)
                    .filter(([cle, v]) => v !== 0 || dossier.soldes.apres[cle] !== v)
                    .map(([cle, v]) => (
                      <tr key={cle} className="border-t border-slate-100">
                        <td className="py-1 pr-4 text-nuit">{LIBELLE_COMPTEUR[cle] ?? cle}</td>
                        <td className="py-1 pr-4 tabular-nums text-nuit/70">{v}</td>
                        <td className="py-1 pr-4 tabular-nums text-jaune">
                          {dossier.soldes.enAttente[cle === 'CP_N1' || cle === 'CP_N' ? 'CP' : cle] ?? 0}
                        </td>
                        <td className="py-1 pr-4 tabular-nums font-semibold text-nuit">
                          {dossier.soldes.apres[cle]}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-nuit/70">
              Ce type d’absence ne consomme aucun compteur : approuver ne changera aucun solde.
            </p>
          )}

          <h3 className="mb-2 mt-4 text-sm font-semibold text-nuit">
            Qui d’autre est absent sur la période
          </h3>
          {dossier.equipe.length === 0 ? (
            <p className="text-sm text-nuit/60">Personne d’autre.</p>
          ) : (
            <ul className="space-y-1 text-sm text-nuit/80">
              {dossier.equipe.map((a, i) => (
                <li key={i}>
                  {a.salarie} ({a.type_libelle}), du {a.date_debut.split('-').reverse().join('/')} au{' '}
                  {a.date_fin.split('-').reverse().join('/')}
                  {a.statut !== 'approuvee' && (
                    <span className="text-jaune"> (en attente)</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {actions.length > 0 && (
        <Section titre="Actions" teinte="vert">
          {!action ? (
            <div className="flex flex-wrap gap-2">
              {actions.map((a) =>
                a.danger ? (
                  <BoutonSecondaire
                    key={a.code}
                    type="button"
                    className="!text-corail !ring-corail-fond"
                    onClick={() => (a.motif ? setAction(a.code) : transiter.mutate({ code: a.code }))}
                  >
                    {a.libelle}
                  </BoutonSecondaire>
                ) : (
                  <BoutonPrincipal
                    key={a.code}
                    type="button"
                    disabled={transiter.isPending}
                    onClick={() => {
                      // Confirmation UNIQUEMENT sur l'irréversible : approuver
                      // décompte des compteurs, annuler ferme la demande.
                      if (a.code === 'T6' && !confirm('Annuler définitivement cette demande ?')) return;
                      transiter.mutate({ code: a.code });
                    }}
                  >
                    {a.libelle}
                  </BoutonPrincipal>
                ),
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Champ
                label="Motif"
                requis
                aide="Le salarié le lira : il doit pouvoir comprendre, et corriger s’il y a lieu."
              >
                <Saisie value={motif} onChange={(e) => setMotif(e.target.value)} autoFocus />
              </Champ>
              <div className="flex gap-2">
                <BoutonSecondaire type="button" onClick={() => { setAction(null); setMotif(''); }}>
                  Annuler
                </BoutonSecondaire>
                <BoutonPrincipal
                  type="button"
                  disabled={!motif.trim() || transiter.isPending}
                  onClick={() => transiter.mutate({ code: action, motif })}
                >
                  Confirmer
                </BoutonPrincipal>
              </div>
            </div>
          )}
        </Section>
      )}

      <Section titre="Chronologie" teinte="violet">
        {chronologie.length === 0 ? (
          <p className="text-sm text-nuit/60">Rien à afficher.</p>
        ) : (
          <ol className="space-y-2 text-sm">
            {[...chronologie]
              .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
              .map((e) => {
                let details: any = null;
                try {
                  details = e.details ? JSON.parse(e.details) : null;
                } catch {
                  details = null;
                }
                return (
                  <li key={e.id} className="border-l-2 border-violet-fond pl-3">
                    <p className="text-nuit">
                      {LIBELLE_EVENEMENT[e.type_evenement] ?? e.type_evenement}
                      {details?.motif ? ` : « ${details.motif} »` : ''}
                    </p>
                    <p className="text-xs text-nuit/50">
                      {new Date(e.created_at).toLocaleString('fr-FR')}
                      {e.acteur_email ? ` · ${e.acteur_email}` : ''}
                    </p>
                  </li>
                );
              })}
          </ol>
        )}
      </Section>

      <div className="pb-8">{retour}</div>
    </div>
  );
}
